// components/Modal/CancelByOrganizerModal.js
import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { TICKET_PAID } from '../../constants/eventTypes';
import WarningOutlineIcon from '../Icons/WarningOutlineIcon';
import Button from '../Button/Button';
import Modal from './Modal';
import InputTextArea from '../Input/InputTextArea';
import { theme } from '../../theme';
import Typography from '../Typography';



const ModalBody = styled.div`
  padding: ${({ theme }) => theme.sizing.sm};
`;

const AttendeeInfo = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.fill.defaultSubtle};
  border: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  padding: ${({ theme }) => theme.sizing.sm};
  border-radius: ${({ theme }) => theme.radius.xs};
  margin-bottom: ${({ theme }) => theme.sizing.sm};
  gap: ${({ theme }) => theme.sizing.xs};
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding-bottom: ${({ theme }) => theme.radius.sms};
  
  &:last-child {
    padding-bottom: 0px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    gap: ${({ theme }) => theme.sizing.xxs};
    flex-basis: 100%;
  }
`;

const WarningMessage = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.fill.warningAlphaMain16};
  border-radius: ${({ theme }) => theme.radius.xs};
  padding: ${({ theme }) => theme.sizing.sm};
  margin-bottom: ${({ theme }) => theme.sizing.md};
`;

const WarningTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.xs};
  padding-bottom: ${({ theme }) => theme.sizing.xs};
`;

const MessageSection = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${({ theme }) => theme.sizing.xs};
`;



const ModalFooter = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.sizing.sm};
  justify-content: space-between;
  padding: ${({ theme }) => theme.sizing.sm};
  border-top: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  background: ${({ theme }) => theme.fill.defaultMain};
  align-self: stretch;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-wrap: wrap;
  }
`;



const CancelByOrganizerModal = ({
  isOpen,
  onClose,
  attendee,
  onConfirm,
  isLoading = false
}) => {
  const { t } = useTranslation('manageEventAttendees');
  const [organizerMessage, setOrganizerMessage] = useState('');

  // Limpiar el mensaje cuando el modal se cierre
  React.useEffect(() => {
    if (!isOpen) {
      setOrganizerMessage('');
    }
  }, [isOpen]);

  if (!isOpen || !attendee) return null;

  const handleConfirm = () => {
    // Pasar attendeeId, refundAmount (null para cancelación por organizador), y message
    const refundAmount = null; // Las cancelaciones por organizador no incluyen reembolso automático
    onConfirm(attendee._id, refundAmount, organizerMessage);
  };

  const handleClose = () => {
    setOrganizerMessage('');
    onClose();
  };

  const isPaidTicket = attendee?.ticketId?.type === TICKET_PAID;

  // Debug: verificar datos del attendee
  // console.log('CancelByOrganizerModal - attendee:', attendee);
  // console.log('CancelByOrganizerModal - ticketId:', attendee?.ticketId);
  // console.log('CancelByOrganizerModal - ticket type:', attendee?.ticketId?.type);
  // console.log('CancelByOrganizerModal - isPaidTicket:', isPaidTicket);

  return (
    <Modal onClose={handleClose} title={t('cancelByOrganizerModal.title')}>
      <ModalBody>
        <AttendeeInfo>
          <InfoRow>
            <Typography as="span" $variant="body-2-medium" color={theme.colors.defaultStrong}>
              {t('cancelByOrganizerModal.attendeeInfo.name')}
            </Typography>
            <Typography as="span" $variant="body-2-regular" color={theme.colors.defaultWeak}>
              {attendee?.userId?.name || t('cancelByOrganizerModal.attendeeInfo.defaultUser')} {attendee?.userId?.lastName || ''}
            </Typography>
          </InfoRow>
          <InfoRow>
            <Typography as="span" $variant="body-2-medium" color={theme.colors.defaultStrong}>
              {t('cancelByOrganizerModal.attendeeInfo.ticket')}
            </Typography>
            <Typography as="span" $variant="body-2-regular" color={theme.colors.defaultWeak}>
              {attendee?.ticketId?.name || t('cancelByOrganizerModal.attendeeInfo.defaultTicket')}
            </Typography>
          </InfoRow>
          {isPaidTicket && (
            <InfoRow>
              <Typography as="span" $variant="body-2-medium" color={theme.colors.defaultStrong}>
                {t('cancelByOrganizerModal.refundSection.amount')}
              </Typography>
              <Typography as="span" $variant="body-2-regular" color={theme.colors.defaultWeak}>
                {attendee?.ticketId?.price || '0'}€
              </Typography>
            </InfoRow>
          )}
        </AttendeeInfo>

        {isPaidTicket && (
          <WarningMessage>
            <WarningTitle>
              <WarningOutlineIcon width={20} height={20} fill={theme.colors.warningMain} />
              <Typography as="p" color={theme.colors.warningMain} $variant="body-1-semibold" $variantTablet="body-2-semibold" $variantMobile="body-2-semibold">
                {t('cancelByOrganizerModal.refundSection.warningTitle')}
              </Typography>
            </WarningTitle>
            <Typography as="p" color={theme.colors.defaultStrong} $variant="body-2-regular">
              {t('cancelByOrganizerModal.refundSection.warningDescription')}
            </Typography>
            <Typography as="p" color={theme.colors.defaultStrong} $variant="body-2-regular" style={{ marginTop: '8px' }}>
              {t('cancelByOrganizerModal.refundSection.helpLink')}
            </Typography>
          </WarningMessage>
        )}

        <MessageSection>
          <Label htmlFor="organizerMessage">
            <Typography as="span" $variant="body-2-medium" color={theme.colors.defaultStrong}>
              {t('cancelByOrganizerModal.organizerMessage.label')}
            </Typography>
          </Label>
          <InputTextArea
            style={{ minHeight: '120px', resize: 'vertical' }}
            id="organizerMessage"
            value={organizerMessage}
            onChange={(e) => setOrganizerMessage(e.target.value)}
            placeholder={t('cancelByOrganizerModal.organizerMessage.placeholder')}
          />
        </MessageSection>
      </ModalBody>

      <ModalFooter>
        <Button
          $variant="ghost"
          type="button"
          onClick={handleClose}
          disabled={isLoading}
          // style={{ flexGrow: 1, flexBasis: 'calc(50% - 8px)' }}
          size="l"
          sizeTablet="l"
          sizeMobile="m"
        >
          {t('cancelByOrganizerModal.buttons.cancel')}
        </Button>
        <Button
          $variant="defaultDanger"
          type="button"
          onClick={handleConfirm}
          disabled={isLoading}
          // style={{ flexGrow: 1, flexBasis: 'calc(50% - 8px)' }}
          size="l"
          sizeTablet="l"
          sizeMobile="m"
        >
          {isLoading ? t('cancelByOrganizerModal.buttons.processing') : t('cancelByOrganizerModal.buttons.confirm')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default CancelByOrganizerModal;